import { Tooltip } from '@chakra-ui/react';
import { Input } from '@sketch-note/design-system/src';
import { ChevronDownIcon, LockedIcon } from '@sketchnote/icons';
import { CsvFileUpload } from '@sketchnote/shared-components';
import { MoreActionMenu, MoreActionMenuProps } from '@sketchnote/shared-components';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useWorkspaceService } from 'src/api/services/workspaces/workspace';
import { PLANRULES } from 'src/constants/plans';
import { useAuth } from 'src/shared/hooks/use-auth';

const { useState, useMemo } = React;

export const CsvImportSelectFile = ({
  onClose,
  showCsvMap,
  selectedWorkspaceId,
}: {
  onClose: () => void;
  showCsvMap: (
    file: File,
    fileTitle: string,
    workspace: { label: string; value: string | undefined }
  ) => void;
  selectedWorkspaceId?: string;
}) => {
  const [fileName, setFileName] = useState('');
  const router = useRouter();
  const { useWorkspaces } = useWorkspaceService();
  const { userWorkspaces: workspaces } = useWorkspaces({});
  const authData = useAuth((s) => s.userDetails);
  const [workspaceName, setWorkspaceName] = useState({
    label: workspaces[0]?.name,
    value: workspaces[0]?.workspaceId,
  });

  useEffect(() => {
    if (selectedWorkspaceId) {
      const selectedWorkspace = workspaces?.find((obj) => obj.workspaceId === selectedWorkspaceId);
      if (selectedWorkspace)
        setWorkspaceName({
          label: selectedWorkspace.name,
          value: selectedWorkspace.workspaceId,
        });
    } else {
      setWorkspaceName({
        label: workspaces && workspaces[0].name,
        value: workspaces && workspaces[0].workspaceId,
      });
    }
  }, [selectedWorkspaceId, workspaces?.length]);

  const otherWorkspaces = useMemo(
    () =>
      workspaces
        .map((obj) =>
          obj.name === 'default-workspace'
            ? {}
            : {
                label: obj.name,
                value: obj.workspaceId,
              }
        )
        .filter((x) => Object.keys(x).length !== 0),

    [router, workspaces]
  );

  const handleSelectWorkspace = (value) => {
    setWorkspaceName({ label: value.label, value: value.value });
  };

  const handleOnImport = (file) => {
    showCsvMap(file, fileName, workspaceName);
    onClose();
  };

  const isRestrictedForUserPlan = useMemo(() => {
    return authData?.plan.type ? !PLANRULES.csvimport[authData?.plan.type] : false;
  }, []);

  return (
    <div className="bg-white rounded-lg">
      <p className="text-2xl text-gray-999 font-medium mb-32">📥 Import</p>
      <div className="flex">
        <div className="ml-12 w-full">
          <label htmlFor="input" className="text-gray-600 text-sm mb-8 block">
            File Name
          </label>
          <input
            className="p-12 bg-gray-100 mb-20 w-full border rounded-md"
            placeholder="Untitled"
            value={fileName}
            onChange={(e) => {
              setFileName(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="flex">
        <div className="ml-12 mb-20 w-full">
          <label className="text-gray-600 text-sm mb-8 block">Import Destination</label>

          <MoreActionMenu
            menuItems={otherWorkspaces as MoreActionMenuProps['menuItems']}
            emptyOptionsText="No workspaces to show"
            classNames={{ labelWrap: 'w-full rounded-sm' }}
            icon={
              <div className="flex flex-row items-center rounded-md border bg-gray-100">
                <Input
                  inputClassName="pl-12 py-12 bg-gray-100"
                  wrapperClassName=" pl-3 w-full border-none"
                  placeholder="Select a workspace"
                  value={workspaceName.label}
                  rightIcon={
                    <ChevronDownIcon wrapperClass=" bg-gray-100 text-xs block text-gray-500 ml-120" />
                  }
                />
              </div>
            }
            onClickMenuItem={handleSelectWorkspace as MoreActionMenuProps['onClickMenuItem']}
          />
        </div>
      </div>

      <div className="flex flex-row relative">
        {isRestrictedForUserPlan && (
          <Tooltip label="This feature is locked in your current plan. Upgrade to Sketchnote Advanced to unlock.">
            <div className="w-full h-full absolute flex justify-center items-center bg-black opacity-50 ml-6 mt-10 rounded-sm cursor-not-allowed">
              <LockedIcon className="text-gray-500" size={30} />
            </div>
          </Tooltip>
        )}
        <CsvFileUpload
          className="mb-8 mt-10"
          translations={{
            attachments_drop_label: 'Attach files',
            attachments_label: 'Attach files',
          }}
          onDrop={handleOnImport}
          maxFileSize={3e7}
          multiple={false}
          acceptTypes={['.csv', '.xlsx']}
          disabled={isRestrictedForUserPlan}
        />
      </div>
    </div>
  );
};
